/**
 * Share-card generator.
 *
 * Renders a branded 1080×1350 (4:5) PNG for a workout PR on <canvas>, returns a Blob.
 * The helper also wraps the Web Share API with a PNG-download fallback so a tap
 * from a mobile browser goes straight to the system share sheet when available.
 */

let fontsPrimed = false;
async function primeFonts() {
  if (fontsPrimed) return;
  // Ensure the Google-hosted fonts are loaded before drawing, otherwise the
  // first render will substitute a fallback face and miss the display type.
  if (typeof document === 'undefined' || !document.fonts?.load) {
    fontsPrimed = true;
    return;
  }
  try {
    await Promise.all([
      document.fonts.load('700 80px "Bebas Neue"'),
      document.fonts.load('700 160px "Bebas Neue"'),
      document.fonts.load('600 36px "Inter"'),
      document.fonts.load('500 28px "Inter"'),
      document.fonts.load('500 48px "JetBrains Mono"'),
    ]);
  } catch {
    /* ignore — canvas will fall back to system fonts */
  }
  fontsPrimed = true;
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Generate a PNG share-card as a Blob.
 *
 * @param {object} opts
 * @param {string} opts.liftName   e.g. "Back Squat"
 * @param {number|string} opts.weight      e.g. 180
 * @param {number|string} [opts.reps]      e.g. 5
 * @param {string} [opts.athleteHandle]    e.g. "johndoe"
 * @param {string} [opts.athleteDisplayName]
 * @param {boolean} [opts.isPR]
 * @param {boolean} [opts.isVerified]
 * @returns {Promise<Blob>}
 */
export async function generateShareCard(opts) {
  await primeFonts();

  const W = 1080;
  const H = 1350;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // --- Background: deep black with subtle radial gold gradient ---
  ctx.fillStyle = '#0A0A0B';
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W * 0.75, H * 0.18, 0, W * 0.75, H * 0.18, W * 0.9);
  glow.addColorStop(0, 'rgba(200, 162, 75, 0.22)');
  glow.addColorStop(0.5, 'rgba(200, 162, 75, 0.06)');
  glow.addColorStop(1, 'rgba(200, 162, 75, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // --- Outer gold plate frame ---
  const padding = 48;
  ctx.strokeStyle = '#C8A24B';
  ctx.lineWidth = 3;
  roundRect(ctx, padding, padding, W - padding * 2, H - padding * 2, 20);
  ctx.stroke();
  // Inset dark stripe
  ctx.strokeStyle = '#0A0A0B';
  ctx.lineWidth = 6;
  roundRect(ctx, padding + 6, padding + 6, W - padding * 2 - 12, H - padding * 2 - 12, 16);
  ctx.stroke();

  // --- Wordmark ---
  ctx.font = '700 56px "Bebas Neue", system-ui';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#F5F5F2';
  ctx.fillText('Bar', padding + 40, padding + 44);
  const barW = ctx.measureText('Bar').width;
  ctx.fillStyle = '#C8A24B';
  ctx.fillText('Chain', padding + 40 + barW, padding + 44);

  // --- Eyebrow label ---
  ctx.font = '500 24px "Inter", system-ui';
  ctx.fillStyle = '#A8ADB4';
  ctx.letterSpacing = '0.12em';
  const eyebrow = opts.isPR ? 'NEW PERSONAL RECORD' : 'LIFT LOGGED';
  ctx.fillText(eyebrow, padding + 40, padding + 180);

  // --- Lift name (hero) ---
  const heroY = 340;
  ctx.font = '700 160px "Bebas Neue", system-ui';
  ctx.fillStyle = '#F5F5F2';
  const liftName = String(opts.liftName || 'Lift').toUpperCase();
  const heroX = padding + 40;
  const heroMaxW = W - (padding + 40) * 2;
  // Shrink to fit if too wide
  let heroFontSize = 160;
  while (heroFontSize > 72 && ctx.measureText(liftName).width > heroMaxW) {
    heroFontSize -= 8;
    ctx.font = `700 ${heroFontSize}px "Bebas Neue", system-ui`;
  }
  ctx.fillText(liftName, heroX, heroY);

  // --- Weight × reps numeric block ---
  const weightStr = `${opts.weight}`;
  const repsStr = opts.reps != null && opts.reps !== '' ? ` × ${opts.reps}` : '';

  ctx.font = '500 220px "JetBrains Mono", ui-monospace, monospace';
  ctx.fillStyle = '#C8A24B';
  ctx.textBaseline = 'alphabetic';
  const kgY = 720;
  ctx.fillText(weightStr, heroX, kgY);
  const weightW = ctx.measureText(weightStr).width;

  // "kg" smaller
  ctx.font = '500 72px "JetBrains Mono", ui-monospace, monospace';
  ctx.fillStyle = '#8A6F2E';
  ctx.fillText(' kg', heroX + weightW, kgY);
  const kgW = ctx.measureText(' kg').width;

  // × reps
  if (repsStr) {
    ctx.font = '500 96px "JetBrains Mono", ui-monospace, monospace';
    ctx.fillStyle = '#F5F5F2';
    ctx.fillText(repsStr, heroX + weightW + kgW, kgY);
  }

  // --- PR badge (top-right) ---
  if (opts.isPR) {
    const bx = W - padding - 40 - 220;
    const by = padding + 40;
    ctx.fillStyle = '#C8A24B';
    roundRect(ctx, bx, by, 220, 70, 35);
    ctx.fill();
    ctx.font = '600 32px "Inter", system-ui';
    ctx.fillStyle = '#0A0A0B';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('★ PR', bx + 110, by + 37);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  // --- Verified stamp ---
  if (opts.isVerified) {
    ctx.strokeStyle = '#3F9B5B';
    ctx.lineWidth = 3;
    const vx = W - padding - 40 - 220;
    const vy = padding + (opts.isPR ? 130 : 40);
    roundRect(ctx, vx, vy, 220, 60, 30);
    ctx.stroke();
    ctx.font = '500 26px "Inter", system-ui';
    ctx.fillStyle = '#3F9B5B';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('VERIFIED', vx + 110, vy + 32);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  // --- Athlete line ---
  const athlete = opts.athleteHandle ? `@${opts.athleteHandle}` : (opts.athleteDisplayName || '');
  if (athlete) {
    ctx.font = '600 36px "Inter", system-ui';
    ctx.fillStyle = '#F5F5F2';
    ctx.fillText(athlete, heroX, H - padding - 140);
  }

  // --- Footer tagline ---
  ctx.font = '500 26px "Inter", system-ui';
  ctx.fillStyle = '#6C727A';
  ctx.fillText('barchain.com — Lift · Log · Verify', heroX, H - padding - 80);

  // --- Export as Blob ---
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/png', 0.95);
  });
}

/**
 * Share a workout — opens the system share sheet if available, otherwise
 * downloads the PNG locally.
 *
 * @param {object} opts (same as generateShareCard)
 * @returns {Promise<'shared' | 'downloaded' | 'cancelled'>}
 */
export async function shareWorkout(opts) {
  const blob = await generateShareCard(opts);
  const fileName = `barchain-${(opts.liftName || 'lift').toLowerCase().replace(/\s+/g, '-')}.png`;
  const text = opts.isPR
    ? `New PR — ${opts.liftName} ${opts.weight}kg${opts.reps ? ` × ${opts.reps}` : ''}`
    : `${opts.liftName} ${opts.weight}kg${opts.reps ? ` × ${opts.reps}` : ''}`;

  // Prefer Web Share API (level 2: with files) when available on this device
  if (typeof navigator !== 'undefined' && navigator.canShare) {
    const file = new File([blob], fileName, { type: 'image/png' });
    if (navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'BarChain',
          text,
        });
        return 'shared';
      } catch (err) {
        if (err?.name === 'AbortError') return 'cancelled';
        // Fall through to download
      }
    }
  }

  // Fallback: download
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return 'downloaded';
}
